import { useCallback, useEffect, useRef, type RefObject } from 'react';

type InfiniteScrollContainer =
    | HTMLElement
    | null
    | RefObject<HTMLElement | null>;

interface UseInfiniteScrollOptions {
    onLoadMore?: () => void;
    isLoading: boolean;
    hasMore: boolean;
    threshold?: number;
    enabled?: boolean;
}

export function useInfiniteScroll(
    container: InfiniteScrollContainer,
    {
        onLoadMore,
        isLoading,
        hasMore,
        threshold = 200,
        enabled = true,
    }: UseInfiniteScrollOptions,
) {
    const onLoadMoreRef = useRef(onLoadMore);
    onLoadMoreRef.current = onLoadMore;

    const getContainer = useCallback(() => {
        if (container && 'current' in container) {
            return container.current;
        }
        return container;
    }, [container]);

    const checkAndLoadMore = useCallback(() => {
        const scrollContainer = getContainer();
        if (!enabled || !scrollContainer || isLoading || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        if (scrollHeight <= clientHeight) return;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        if (distanceFromBottom < threshold) {
            onLoadMoreRef.current?.();
        }
    }, [enabled, getContainer, isLoading, hasMore, threshold]);

    useEffect(() => {
        if (!enabled || isLoading || !hasMore) return;

        const rafId = requestAnimationFrame(() => {
            checkAndLoadMore();
        });

        return () => cancelAnimationFrame(rafId);
    }, [enabled, isLoading, hasMore, checkAndLoadMore]);

    useEffect(() => {
        const scrollContainer = getContainer();
        if (!enabled || !scrollContainer) return;

        scrollContainer.addEventListener('scroll', checkAndLoadMore);
        return () =>
            scrollContainer.removeEventListener('scroll', checkAndLoadMore);
    }, [enabled, getContainer, checkAndLoadMore]);
}
